import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Chip, Divider, Drawer, IconButton, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import { ETYKIETA_ZAKRESU, type Skorka, type ZakresPaska, type ZastosowanieSkorki } from '@/lib/api/tresci';
import { chwilaWarszawa, oknoWarszawa, opisAdresatow } from '@/lib/tresci/biblioteka';
import {
  LITERA_ZAKRESU, TORY, adresaciKrotko, czasTrwania, dniOsi, kolizjeBloku, kpi, pozycja, ulozWiersze, wZakresie, wykryjKolizje,
  zbudujBloki, type BlokHarmonogramu, type DzienOsi, type Kolizja,
} from '@/lib/tresci/harmonogramSkorek';
import { etykietaOsoby, useOsobyPelne } from '@/lib/tresci/osobySkorek';
import { utcNaWarszawe } from '@/lib/tresci/czasWarszawa';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * HARMONOGRAM SKÓREK (OWNER 21.10) — u góry zakładki „Skórki”, nad biblioteką
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Wariant: poziomy harmonogram w czterech torach (Wszyscy / Miasta / Szkoły / Osoby), bo pytanie operatora
 * brzmi „co, GDZIE i KIEDY” — tor odpowiada „gdzie”, oś „kiedy”, kolor „co” (kolor = skórka, nie zakres).
 * Nachodzące bloki w jednym torze idą pod sobą. Klik w blok → panel boczny ze szczegółem i akcjami, które
 * prowadzą do ISTNIEJĄCYCH okien biblioteki (historia, zarządzanie, wyłączenie, widok użytkownika) —
 * harmonogram niczego nie zapisuje sam i nie liczy na nowo, kto co zobaczy.
 */

const ZAKRESY_DNI = [7, 14, 30, 60] as const;
type ZakresDni = (typeof ZAKRESY_DNI)[number];
type FiltrStanu = 'biezace' | 'aktywne' | 'zaplanowane' | 'wygasle' | 'wszystkie';

const FILTRY_STANU: { wartosc: FiltrStanu; etykieta: string }[] = [
  { wartosc: 'biezace', etykieta: 'Aktywne i zaplanowane' },
  { wartosc: 'aktywne', etykieta: 'Aktywne teraz' },
  { wartosc: 'zaplanowane', etykieta: 'Zaplanowane' },
  { wartosc: 'wygasle', etykieta: 'Wygasłe i wyłączone' },
  { wartosc: 'wszystkie', etykieta: 'Wszystkie' },
];

const STAN_BLOKU: Record<BlokHarmonogramu['stan'], { tekst: string; kolor: 'success' | 'info' | 'default' }> = {
  ACTIVE: { tekst: 'Aktywna teraz', kolor: 'success' },
  SCHEDULED: { tekst: 'Zaplanowana', kolor: 'info' },
  ENDED: { tekst: 'Zakończona', kolor: 'default' },
  DISABLED: { tekst: 'Wyłączona', kolor: 'default' },
};

const DNI_TYGODNIA = ['pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd'];
const MIESIACE = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
const WYSOKOSC_WIERSZA = 30;
const SZEROKOSC_TORU = 112;
/** Blok węższy niż ten ułamek osi ma etykietę OBOK siebie (np. kilkugodzinne „Dzień bez Maila” w widoku 30 dni). */
const WASKI = 0.08;
/** Miejsce rezerwowane w wierszu na etykietę obok wąskiego bloku (ułamek osi). */
const MIEJSCE_ETYKIETY = 0.26;
const DOBA = 86_400_000;

const dwa = (n: number) => String(n).padStart(2, '0');
const dataKrotko = (t: number) => { const l = utcNaWarszawe(new Date(t)); return `${dwa(l.dzien)}.${dwa(l.miesiac)}`; };
const iso = (t: number | null) => (t === null ? null : new Date(t).toISOString());

function pasujeStan(b: BlokHarmonogramu, f: FiltrStanu): boolean {
  if (f === 'wszystkie') return true;
  if (f === 'biezace') return b.stan === 'ACTIVE' || b.stan === 'SCHEDULED';
  if (f === 'aktywne') return b.stan === 'ACTIVE';
  if (f === 'zaplanowane') return b.stan === 'SCHEDULED';
  return b.stan === 'ENDED' || b.stan === 'DISABLED';
}

/** Akcje szczegółu — wszystkie prowadzą do okien, które biblioteka już ma. */
export interface AkcjeHarmonogramu {
  historiaZastosowania: (z: ZastosowanieSkorki) => void;
  historiaSkorki: (themeId: string) => void;
  zarzadzaj: (themeId: string) => void;
  zaplanuj: (themeId: string, zakres: ZakresPaska) => void;
  wylacz: (z: ZastosowanieSkorki) => Promise<boolean>;
  wylaczV1: (s: Skorka) => Promise<boolean>;
  widokUzytkownika: () => void;
  pokazWBibliotece: (themeId: string) => void;
  podglad: (revisionId: number, nazwa: string, minSilnik: number) => void;
  /** Skórka ma pozycję w bibliotece (nie tylko rewizje z błędem) — inaczej akcje biblioteki są ukryte. */
  wBibliotece: (themeId: string) => boolean;
}

export interface HarmonogramSkorekProps {
  zastosowania: readonly ZastosowanieSkorki[];
  rewizje: readonly Skorka[];
  mozePublikowac: boolean;
  zapisDostepny: boolean;
  akcje: AkcjeHarmonogramu;
  /** Chwila „teraz” (testy); domyślnie zegar przeglądarki. */
  teraz?: Date;
}

export function HarmonogramSkorek({ zastosowania, rewizje, mozePublikowac, zapisDostepny, akcje, teraz: terazZewn }: HarmonogramSkorekProps) {
  const [teraz] = useState(() => terazZewn ?? new Date());
  const [liczbaDni, setLiczbaDni] = useState<ZakresDni>(30);
  const [przesuniecie, setPrzesuniecie] = useState(0);
  const [filtr, setFiltr] = useState<FiltrStanu>('biezace');
  const [tory, setTory] = useState<ZakresPaska[]>(TORY.map((t) => t.zakres));
  const [skorka, setSkorka] = useState('');
  const [szukaj, setSzukaj] = useState('');
  const [tylkoKolizje, setTylkoKolizje] = useState(false);
  const [zwiniete, setZwiniete] = useState<ZakresPaska[]>([]);
  const [wybrany, setWybrany] = useState<string | null>(null);

  const bloki = useMemo(() => zbudujBloki(zastosowania, rewizje), [zastosowania, rewizje]);
  const kolizje = useMemo(() => wykryjKolizje(bloki), [bloki]);
  const wKolizji = useMemo(() => new Set(kolizje.flatMap((k) => [k.a, k.b])), [kolizje]);
  const liczby = useMemo(() => kpi(bloki, kolizje, teraz), [bloki, kolizje, teraz]);
  const dni = useMemo(() => dniOsi(teraz, liczbaDni, przesuniecie), [teraz, liczbaDni, przesuniecie]);
  const skorki = useMemo(() => [...new Map(bloki.map((b) => [b.themeId, b])).values()]
    .sort((a, b) => a.nazwa.localeCompare(b.nazwa, 'pl')), [bloki]);

  const fraza = szukaj.trim().toLowerCase();
  const widoczne = bloki.filter((b) => pasujeStan(b, filtr) && tory.includes(b.zakres)
    && (!skorka || b.themeId === skorka) && (!tylkoKolizje || wKolizji.has(b.klucz))
    && (!fraza || b.nazwa.toLowerCase().includes(fraza) || b.adresaci.some((t) => t.name.toLowerCase().includes(fraza)))
    && wZakresie(b, dni));
  const poza = bloki.filter((b) => pasujeStan(b, filtr) && !wZakresie(b, dni)).length;
  const blokWybrany = bloki.find((b) => b.klucz === wybrany) ?? null;

  /** Przesunięcie, w którym chwila `t` jest na osi (skok do bloku spoza zakresu). */
  const skoczDo = (t: number) => {
    const dzis = dniOsi(teraz, 1)[0]!.start;
    const roznica = Math.floor((t - dzis) / DOBA);
    setPrzesuniecie(Math.floor((roznica + Math.floor(liczbaDni / 2)) / liczbaDni));
  };
  const otworz = (b: BlokHarmonogramu) => { if (!wZakresie(b, dni)) skoczDo(b.start); setWybrany(b.klucz); };
  const pokazWszystko = () => {
    setFiltr('wszystkie'); setTory(TORY.map((t) => t.zakres)); setSkorka(''); setSzukaj(''); setTylkoKolizje(false); setZwiniete([]);
  };

  const pierwszy = dni[0]!; const ostatni = dni[dni.length - 1]!;
  const opisZakresu = `${dwa(pierwszy.dzien)}.${dwa(pierwszy.miesiac)} – ${dwa(ostatni.dzien)}.${dwa(ostatni.miesiac)}.${ostatni.rok}`;
  const dzisNaOsi = teraz.getTime() >= pierwszy.start && teraz.getTime() < ostatni.koniec ? pozycja(teraz.getTime(), dni) : null;

  return (
    <Box component="section" aria-labelledby="harmonogram-skorek-tytul" data-testid="harmonogram-skorek"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, mb: 1.5 }}>
        <Box>
          <Typography id="harmonogram-skorek-tytul" variant="h6" component="h2">Harmonogram skórek</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Kolor = skórka · tor = gdzie · oś = kiedy (czas warszawski). Kliknij blok, żeby zobaczyć szczegóły.
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          <Button size="small" variant="outlined" onClick={() => setPrzesuniecie(0)}>Dziś</Button>
          <Button size="small" variant={filtr === 'aktywne' ? 'contained' : 'outlined'} onClick={() => setFiltr(filtr === 'aktywne' ? 'biezace' : 'aktywne')}>
            Tylko aktywne
          </Button>
          <Button size="small" variant="outlined" onClick={pokazWszystko}>Pokaż wszystko</Button>
          <Button size="small" variant={tylkoKolizje ? 'contained' : 'outlined'} color={liczby.kolizje ? 'error' : 'inherit'}
            onClick={() => setTylkoKolizje((x) => !x)} aria-pressed={tylkoKolizje}>
            Kolizje ({liczby.kolizje})
          </Button>
        </Stack>
      </Stack>

      {/* B. KPI — cały system, niezależnie od filtrów widoku */}
      <Box data-testid="harmonogram-kpi" sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', mb: 1.5 }}>
        <Kafel etykieta="Aktywne teraz" wartosc={liczby.aktywne} kolor="success.main" />
        <Kafel etykieta="Zaplanowane" wartosc={liczby.zaplanowane} kolor="info.main" />
        <Kafel etykieta="Bez końca" wartosc={liczby.bezKonca} kolor={liczby.bezKonca ? 'warning.main' : undefined} />
        <Kafel etykieta="Kolizje" wartosc={liczby.kolizje} kolor={liczby.kolizje ? 'error.main' : undefined}
          dopisek={liczby.remisy ? `${liczby.remisy} z równym priorytetem` : undefined} />
        <Kafel etykieta="Najbliższy start" tekst={liczby.najblizszeStarty[0] ? `${dataKrotko(liczby.najblizszeStarty[0].start)} · ${liczby.najblizszeStarty[0].nazwa}` : 'brak planu'} />
        <Kafel etykieta="Najbliższy koniec" tekst={liczby.najblizszeKonce[0] ? `${dataKrotko(liczby.najblizszeKonce[0].koniec!)} · ${liczby.najblizszeKonce[0].nazwa}` : 'brak'} />
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1.5 }} data-testid="najblizsze-zdarzenia">
        <ListaZdarzen tytul="Najbliższe starty" bloki={liczby.najblizszeStarty} czas={(b) => b.start} onWybierz={otworz} />
        <ListaZdarzen tytul="Najbliższe końce" bloki={liczby.najblizszeKonce} czas={(b) => b.koniec!} onWybierz={otworz} />
      </Stack>

      {/* A. PASEK STERUJĄCY */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <ToggleButtonGroup exclusive size="small" value={liczbaDni} aria-label="Zakres osi czasu"
          onChange={(_, v: ZakresDni | null) => { if (v) { setLiczbaDni(v); setPrzesuniecie(0); } }}>
          {ZAKRESY_DNI.map((n) => <ToggleButton key={n} value={n} aria-label={`${n} dni`}>{n} dni</ToggleButton>)}
        </ToggleButtonGroup>
        <Button size="small" onClick={() => setPrzesuniecie((x) => x - 1)} aria-label="Poprzedni zakres">‹ Poprzedni</Button>
        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 150, textAlign: 'center' }} data-testid="zakres-osi">{opisZakresu}</Typography>
        <Button size="small" onClick={() => setPrzesuniecie((x) => x + 1)} aria-label="Następny zakres">Następny ›</Button>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField select size="small" label="Stan" value={filtr} onChange={(e) => setFiltr(e.target.value as FiltrStanu)} sx={{ minWidth: 200 }}>
          {FILTRY_STANU.map((f) => <MenuItem key={f.wartosc} value={f.wartosc}>{f.etykieta}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup size="small" value={tory} aria-label="Tory zakresu"
          onChange={(_, v: ZakresPaska[]) => { if (v.length) setTory(v); }}>
          {TORY.map((t) => <ToggleButton key={t.zakres} value={t.zakres} aria-label={t.etykieta}>{t.etykieta}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField select size="small" label="Skórka" value={skorka} onChange={(e) => setSkorka(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Wszystkie skórki</MenuItem>
          {skorki.map((s) => <MenuItem key={s.themeId} value={s.themeId}>{s.nazwa}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Szukaj: miasto, szkoła, osoba" value={szukaj} onChange={(e) => setSzukaj(e.target.value)} sx={{ minWidth: 230 }} />
      </Stack>

      {/* C. OŚ CZASU */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: liczbaDni > 30 ? 960 : 720 }} data-testid="harmonogram-os">
          <NaglowekOsi dni={dni} dzis={dzisNaOsi} />
          {TORY.filter((t) => tory.includes(t.zakres)).map((t) => (
            <Tor key={t.zakres} zakres={t.zakres} etykieta={t.etykieta} dni={dni} dzis={dzisNaOsi}
              bloki={widoczne.filter((b) => b.zakres === t.zakres)} wKolizji={wKolizji} kolizje={kolizje} wybrany={wybrany}
              zwiniety={zwiniete.includes(t.zakres)}
              onZwin={() => setZwiniete((z) => (z.includes(t.zakres) ? z.filter((x) => x !== t.zakres) : [...z, t.zakres]))}
              onWybierz={(b) => setWybrany(b.klucz)} />
          ))}
        </Box>
      </Box>
      {widoczne.length === 0 ? (
        <Alert severity="info" role="status" sx={{ mt: 1 }} data-testid="harmonogram-pusto"
          action={liczby.najblizszeStarty[0] && !wZakresie(liczby.najblizszeStarty[0], dni) ? (
            <Button size="small" onClick={() => skoczDo(liczby.najblizszeStarty[0]!.start)}>Pokaż najbliższy start</Button>
          ) : null}>
          W tym zakresie dat i przy tych filtrach nie ma zastosowań{poza ? ` (poza zakresem: ${poza})` : ''}.
        </Alert>
      ) : null}

      <Legenda bloki={widoczne} />

      {blokWybrany ? (
        <SzczegolBloku b={blokWybrany} bloki={bloki} kolizje={kolizje} mozePublikowac={mozePublikowac} zapisDostepny={zapisDostepny}
          akcje={akcje} onZamknij={() => setWybrany(null)} onWybierz={otworz} />
      ) : null}
    </Box>
  );
}

function Kafel({ etykieta, wartosc, tekst, kolor, dopisek }: { etykieta: string; wartosc?: number; tekst?: string; kolor?: string; dopisek?: string }) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, px: 1.25, py: 0.75 }} data-testid={`kpi-${etykieta}`}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{etykieta}</Typography>
      {wartosc !== undefined
        ? <Typography variant="h6" component="p" sx={{ fontWeight: 800, color: kolor ?? 'text.primary', lineHeight: 1.2 }}>{wartosc}</Typography>
        : <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={tekst}>{tekst}</Typography>}
      {dopisek ? <Typography variant="caption" sx={{ color: 'error.main' }}>{dopisek}</Typography> : null}
    </Box>
  );
}

function ListaZdarzen({ tytul, bloki, czas, onWybierz }: {
  tytul: string; bloki: BlokHarmonogramu[]; czas: (b: BlokHarmonogramu) => number; onWybierz: (b: BlokHarmonogramu) => void;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overline" sx={{ lineHeight: 1.6 }}>{tytul}</Typography>
      {bloki.length === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>brak</Typography> : (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {bloki.map((b) => (
            <Chip key={b.klucz} size="small" clickable onClick={() => onWybierz(b)} variant="outlined"
              icon={<Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: b.kolor, ml: '6px !important' }} />}
              label={`${dataKrotko(czas(b))} · ${b.nazwa} · ${LITERA_ZAKRESU[b.zakres]} ${adresaciKrotko(b)}`} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function NaglowekOsi({ dni, dzis }: { dni: DzienOsi[]; dzis: number | null }) {
  const n = dni.length;
  // Miesiące nad dniami: odcinek od pierwszego dnia miesiąca w zakresie.
  const miesiace: { klucz: string; od: number; ile: number; nazwa: string }[] = [];
  dni.forEach((d, i) => {
    const ost = miesiace[miesiace.length - 1];
    if (ost && ost.klucz === `${d.rok}-${d.miesiac}`) ost.ile++;
    else miesiace.push({ klucz: `${d.rok}-${d.miesiac}`, od: i, ile: 1, nazwa: `${MIESIACE[d.miesiac - 1]} ${d.rok}` });
  });
  const etykietaDnia = (d: DzienOsi) => {
    if (n <= 14) return `${DNI_TYGODNIA[d.dzienTygodnia]} ${dwa(d.dzien)}.${dwa(d.miesiac)}`;
    if (n <= 30) return String(d.dzien);
    return d.dzienTygodnia === 0 || d.dzien === 1 || d.dzis ? String(d.dzien) : '';
  };
  return (
    <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ width: SZEROKOSC_TORU, flexShrink: 0 }} />
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ display: 'flex' }}>
          {miesiace.map((m) => (
            <Typography key={m.klucz} variant="caption" noWrap
              sx={{ width: `${(m.ile / n) * 100}%`, fontWeight: 700, borderLeft: m.od ? 1 : 0, borderColor: 'divider', pl: 0.5 }}>
              {m.ile >= 3 ? m.nazwa : ''}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'flex' }}>
          {dni.map((d) => (
            <Typography key={d.start} variant="caption" noWrap data-testid={d.dzis ? 'dzien-dzis' : undefined}
              sx={{
                flex: 1, minWidth: 0, textAlign: 'center', py: 0.25, fontSize: n > 30 ? 10 : 11,
                color: d.dzis ? 'primary.contrastText' : d.weekend ? 'text.secondary' : 'text.primary',
                bgcolor: d.dzis ? 'primary.main' : d.weekend ? 'action.hover' : undefined, fontWeight: d.dzis ? 800 : 400,
              }}>
              {etykietaDnia(d)}
            </Typography>
          ))}
        </Box>
        {dzis !== null ? (
          <Typography variant="caption" data-testid="linia-dzis-etykieta"
            sx={{ position: 'absolute', top: 0, left: `${dzis * 100}%`, transform: 'translateX(-50%)', bgcolor: 'error.main', color: '#fff', px: 0.5, borderRadius: 1, fontWeight: 800, fontSize: 10 }}>
            DZIŚ
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function Tor({ zakres, etykieta, dni, dzis, bloki, wKolizji, kolizje, wybrany, zwiniety, onZwin, onWybierz }: {
  zakres: ZakresPaska; etykieta: string; dni: DzienOsi[]; dzis: number | null; bloki: BlokHarmonogramu[]; wKolizji: Set<string>;
  kolizje: Kolizja[]; wybrany: string | null; zwiniety: boolean; onZwin: () => void; onWybierz: (b: BlokHarmonogramu) => void;
}) {
  const pierwszy = dni[0]!; const ostatni = dni[dni.length - 1]!;
  const os = ostatni.koniec - pierwszy.start;
  const geometria = new Map(bloki.map((b) => {
    const lewo = pozycja(b.start, dni);
    const prawo = b.koniecRysowany === null ? 1 : pozycja(b.koniecRysowany, dni);
    const waski = prawo - lewo < WASKI;
    // Przy prawej krawędzi etykieta idzie na lewo od bloku, żeby nie wychodziła poza oś.
    const obok: 'prawo' | 'lewo' | null = waski ? (prawo + MIEJSCE_ETYKIETY > 1 ? 'lewo' : 'prawo') : null;
    return [b.klucz, { lewo, prawo, obok }];
  }));
  const wiersze = ulozWiersze(bloki, (b) => {
    const k = b.koniecRysowany ?? Infinity;
    const g = geometria.get(b.klucz);
    if (g?.obok === 'prawo') return [b.start, Math.max(k, b.start + MIEJSCE_ETYKIETY * os)];
    if (g?.obok === 'lewo') return [b.start - MIEJSCE_ETYKIETY * os, k];
    return [b.start, k];
  });
  const liczbaWierszy = Math.max(1, ...[...wiersze.values()].map((w) => w + 1));
  const wysokosc = zwiniety ? 12 : liczbaWierszy * (WYSOKOSC_WIERSZA + 4) + 4;
  return (
    <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }} data-testid={`tor-${zakres}`}>
      <Box sx={{ width: SZEROKOSC_TORU, flexShrink: 0, pr: 1, py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <IconButton size="small" onClick={onZwin} aria-label={`${zwiniety ? 'Rozwiń' : 'Zwiń'} tor ${etykieta}`} aria-expanded={!zwiniety} sx={{ p: 0.25 }}>
          <Box component="span" sx={{ fontSize: 12, width: 14, display: 'inline-block' }}>{zwiniety ? '▸' : '▾'}</Box>
        </IconButton>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
            <Box component="span" sx={{ display: 'inline-grid', placeItems: 'center', width: 18, height: 18, mr: 0.5, borderRadius: 0.5, bgcolor: 'text.primary', color: 'background.paper', fontSize: 11 }}>
              {LITERA_ZAKRESU[zakres]}
            </Box>
            {etykieta}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{bloki.length}</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, position: 'relative', height: wysokosc, minHeight: zwiniety ? 12 : 38 }}>
        {/* tło dni: weekendy i dziś */}
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex' }} aria-hidden>
          {dni.map((d) => (
            <Box key={d.start} sx={{ flex: 1, borderLeft: 1, borderColor: 'divider', bgcolor: d.weekend ? 'action.hover' : undefined, opacity: d.weekend ? 0.7 : 1 }} />
          ))}
        </Box>
        {dzis !== null ? (
          <Box aria-hidden data-testid={`linia-dzis-${zakres}`}
            sx={{ position: 'absolute', top: 0, bottom: 0, left: `${dzis * 100}%`, width: 2, bgcolor: 'error.main', zIndex: 2, transform: 'translateX(-1px)' }} />
        ) : null}
        {!zwiniety ? bloki.map((b) => {
          const { lewo, prawo, obok } = geometria.get(b.klucz)!;
          const uciety = b.start < pierwszy.start;
          const dalej = b.koniecRysowany === null || b.koniecRysowany > ostatni.koniec;
          return (
            <Blok key={b.klucz} b={b} lewo={lewo} szerokosc={Math.max(prawo - lewo, 0)} wiersz={wiersze.get(b.klucz) ?? 0}
              uciety={uciety} dalej={dalej} kolizja={wKolizji.has(b.klucz)} remis={kolizjeBloku(b.klucz, kolizje).some((k) => k.remis)}
              wybrany={wybrany === b.klucz} obok={obok} onWybierz={() => onWybierz(b)} />
          );
        }) : null}
        {!zwiniety && bloki.length === 0 ? (
          <Typography variant="caption" sx={{ position: 'absolute', left: 8, top: 10, color: 'text.secondary' }}>—</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function opisBloku(b: BlokHarmonogramu): string {
  const gdzie = b.v1 ? 'Wszyscy (publikacja V1)' : `${ETYKIETA_ZAKRESU[b.zakres]}: ${b.zastosowanie ? opisAdresatow(b.zastosowanie, 4) : '—'}`;
  return `${b.nazwa} · ${gdzie} · ${oknoWarszawa(iso(b.start), iso(b.koniec))} · ${czasTrwania(b.start, b.koniec)} · ${STAN_BLOKU[b.stan].tekst}`;
}

function Blok({ b, lewo, szerokosc, wiersz, uciety, dalej, kolizja, remis, wybrany, obok, onWybierz }: {
  b: BlokHarmonogramu; lewo: number; szerokosc: number; wiersz: number; uciety: boolean; dalej: boolean; kolizja: boolean; remis: boolean;
  wybrany: boolean; obok: 'prawo' | 'lewo' | null; onWybierz: () => void;
}) {
  const gora = 4 + wiersz * (WYSOKOSC_WIERSZA + 4);
  const godziny = (t: number) => { const l = utcNaWarszawe(new Date(t)); return `${dwa(l.godzina)}:${dwa(l.minuta)}`; };
  const terminObok = b.koniec === null ? `od ${dataKrotko(b.start)} ${godziny(b.start)}`
    : `${dataKrotko(b.start)} ${godziny(b.start)}–${dataKrotko(b.koniec) === dataKrotko(b.start) ? '' : `${dataKrotko(b.koniec)} `}${godziny(b.koniec)}`;
  const aktywny = b.stan === 'ACTIVE';
  const plan = b.stan === 'SCHEDULED';
  const wygasly = !aktywny && !plan;
  const tlo = aktywny ? b.kolor : plan ? `${b.kolor}55` : `repeating-linear-gradient(135deg, ${b.kolor}33 0 6px, transparent 6px 12px)`;
  const podpowiedz = (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>{b.nazwa}</Typography>
      <Typography variant="caption" component="div">
        {b.v1 ? 'Wszyscy (publikacja V1)' : `${ETYKIETA_ZAKRESU[b.zakres]}: ${b.zastosowanie ? opisAdresatow(b.zastosowanie, 4) : '—'}`}
      </Typography>
      <Typography variant="caption" component="div">Start: {chwilaWarszawa(iso(b.start))}</Typography>
      <Typography variant="caption" component="div">Koniec: {b.koniec === null ? 'bez końca' : chwilaWarszawa(iso(b.koniec))}</Typography>
      <Typography variant="caption" component="div">Czas trwania: {czasTrwania(b.start, b.koniec)} · {STAN_BLOKU[b.stan].tekst}</Typography>
      {kolizja ? <Typography variant="caption" component="div" sx={{ fontWeight: 800 }}>⚠ Kolizja{remis ? ' — ten sam priorytet' : ''}</Typography> : null}
    </Box>
  );
  return (
    <>
    <Tooltip title={podpowiedz} placement="top" arrow disableInteractive>
      <ButtonBase onClick={onWybierz} aria-label={opisBloku(b)} data-testid={`blok-${b.klucz}`} data-stan={b.stan}
        data-kolor={b.kolor} data-kolizja={kolizja ? 'tak' : undefined}
        sx={{
          position: 'absolute', top: gora, height: WYSOKOSC_WIERSZA, zIndex: aktywny ? 3 : 1,
          left: `${lewo * 100}%`, width: `calc(${szerokosc * 100}% - 2px)`, minWidth: obok ? 22 : 8,
          background: tlo, color: aktywny ? b.kolorTekstu : 'text.primary', opacity: wygasly ? 0.65 : 1,
          border: aktywny ? '2px solid' : '1.5px solid', borderColor: kolizja ? 'error.main' : aktywny ? 'text.primary' : b.kolor,
          borderStyle: wygasly ? 'dashed' : 'solid', borderRightStyle: b.koniec === null ? 'dashed' : undefined,
          borderRadius: uciety ? '0 6px 6px 0' : dalej ? '6px 0 0 6px' : '6px',
          boxShadow: wybrany ? '0 0 0 3px rgba(247,177,11,0.8)' : aktywny ? '0 1px 4px rgba(25,25,35,0.25)' : 'none',
          outline: kolizja ? '1px solid' : 'none', outlineColor: 'error.main',
          justifyContent: 'flex-start', px: 0.5, gap: 0.5, overflow: 'hidden', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700,
          '&:hover': { filter: 'brightness(0.95)', zIndex: 4 }, '&:focus-visible': { boxShadow: '0 0 0 3px rgba(247,177,11,0.9)' },
        }}>
        {uciety ? <span aria-hidden>‹</span> : null}
        <Box component="span" aria-hidden sx={{
          flexShrink: 0, display: 'inline-grid', placeItems: 'center', width: 16, height: 16, borderRadius: 0.5, fontSize: 10, fontWeight: 800,
          bgcolor: 'rgba(25,25,35,0.78)', color: '#fff',
        }}>{LITERA_ZAKRESU[b.zakres]}</Box>
        {kolizja && !obok ? <span aria-hidden>⚠</span> : null}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: obok ? 'none' : undefined }}>
          {b.nazwa}{szerokosc > 0.12 ? ` · ${b.v1 ? 'V1' : adresaciKrotko(b)}` : ''}
          {szerokosc > 0.22 ? ` · ${dataKrotko(b.start)}–${b.koniec === null ? '' : dataKrotko(b.koniec)}` : ''}
        </Box>
        {b.koniec === null ? <Box component="span" aria-hidden sx={{ ml: 'auto', fontWeight: 900 }}>→</Box> : dalej ? <Box component="span" aria-hidden sx={{ ml: 'auto' }}>›</Box> : null}
      </ButtonBase>
    </Tooltip>
    {obok ? (
      <Box component="span" aria-hidden onClick={onWybierz} data-testid={`etykieta-obok-${b.klucz}`}
        sx={{
          position: 'absolute', top: gora, height: WYSOKOSC_WIERSZA, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          maxWidth: `${MIEJSCE_ETYKIETY * 100}%`, overflow: 'hidden', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, zIndex: 2, px: 0.5,
          ...(obok === 'prawo'
            ? { left: `calc(${lewo * 100}% + max(${szerokosc * 100}%, 22px) + 4px)` }
            : { right: `calc(${(1 - lewo) * 100}% + 4px)`, justifyContent: 'flex-end' }),
        }}>
        {kolizja ? <span>⚠</span> : null}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{terminObok} ·</Box> {b.nazwa}
        </Box>
      </Box>
    ) : null}
    </>
  );
}

function Legenda({ bloki }: { bloki: BlokHarmonogramu[] }) {
  const skorki = [...new Map(bloki.map((b) => [b.themeId, b])).values()];
  const wzor = (sx: object) => <Box component="span" aria-hidden sx={{ display: 'inline-block', width: 26, height: 14, borderRadius: 1, verticalAlign: 'middle', mr: 0.5, ...sx }} />;
  return (
    <Box sx={{ mt: 1.5 }} data-testid="harmonogram-legenda">
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Typography variant="caption">{wzor({ bgcolor: '#729fcf', border: '2px solid', borderColor: 'text.primary' })}aktywna teraz</Typography>
        <Typography variant="caption">{wzor({ bgcolor: '#729fcf55', border: '1.5px solid #729fcf' })}zaplanowana</Typography>
        <Typography variant="caption">{wzor({ background: 'repeating-linear-gradient(135deg, #729fcf33 0 6px, transparent 6px 12px)', border: '1.5px dashed #729fcf' })}wygasła / wyłączona</Typography>
        <Typography variant="caption"><b>→</b> bez końca</Typography>
        <Typography variant="caption"><b>‹ ›</b> trwa poza zakresem</Typography>
        <Typography variant="caption">{wzor({ border: '2px solid', borderColor: 'error.main' })}⚠ kolizja</Typography>
        <Typography variant="caption">{wzor({ width: 3, bgcolor: 'error.main' })}linia DZIŚ</Typography>
        <Typography variant="caption"><b>W</b> wszyscy · <b>M</b> miasto · <b>S</b> szkoła · <b>O</b> osoby</Typography>
      </Stack>
      {skorki.length ? (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5, mt: 0.75 }} aria-label="Kolory skórek">
          {skorki.map((b) => (
            <Typography key={b.themeId} variant="caption" data-testid={`kolor-skorki-${b.themeId}`}>
              {wzor({ bgcolor: b.kolor, width: 12, height: 12, borderRadius: '50%' })}{b.nazwa}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function SzczegolBloku({ b, bloki, kolizje, mozePublikowac, zapisDostepny, akcje, onZamknij, onWybierz }: {
  b: BlokHarmonogramu; bloki: BlokHarmonogramu[]; kolizje: Kolizja[]; mozePublikowac: boolean; zapisDostepny: boolean;
  akcje: AkcjeHarmonogramu; onZamknij: () => void; onWybierz: (b: BlokHarmonogramu) => void;
}) {
  const [trwa, setTrwa] = useState(false);
  const z = b.zastosowanie;
  const osoby = useOsobyPelne(z && z.scope === 'USER' ? z.targets.map((t) => t.id) : []);
  const wBibliotece = akcje.wBibliotece(b.themeId);
  const mojeKolizje = kolizjeBloku(b.klucz, kolizje);
  const biezacy = b.stan === 'ACTIVE' || b.stan === 'SCHEDULED';
  const wylaczono = z?.disabledAt ?? b.v1?.disabledAt ?? null;
  const i = (cel: () => void) => () => { onZamknij(); cel(); };

  const wylacz = async () => {
    setTrwa(true);
    const ok = z ? await akcje.wylacz(z) : b.v1 ? await akcje.wylaczV1(b.v1) : false;
    setTrwa(false);
    if (ok) onZamknij();
  };

  return (
    <Drawer anchor="right" open onClose={onZamknij} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 } } } }}>
      <Box role="dialog" aria-labelledby="szczegol-bloku-tytul" data-testid="szczegol-bloku" sx={{ p: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography id="szczegol-bloku-tytul" variant="h6" component="h2">{b.nazwa}</Typography>
          <Button size="small" onClick={onZamknij}>Zamknij</Button>
        </Stack>
        <Box sx={{ height: 120, display: 'grid', placeItems: 'center', bgcolor: '#1d1b1a', borderRadius: 1, overflow: 'hidden', mb: 1.5, borderBottom: `6px solid ${b.kolor}` }}>
          {b.miniatura
            ? <img src={b.miniatura} alt={`Miniatura: ${b.nazwa}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <Typography sx={{ color: '#FFD400', fontWeight: 800, fontSize: 28 }}>{b.nazwa.slice(0, 2).toUpperCase()}</Typography>}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip size="small" color={STAN_BLOKU[b.stan].kolor} label={STAN_BLOKU[b.stan].tekst} />
          <Chip size="small" variant="outlined" label={b.v1 ? 'Wszyscy (publikacja V1)' : ETYKIETA_ZAKRESU[b.zakres]} />
          {b.koniec === null ? <Chip size="small" color="warning" variant="outlined" label="Bez końca" /> : null}
          {mojeKolizje.length ? <Chip size="small" color="error" label={`Kolizje: ${mojeKolizje.length}`} /> : null}
        </Stack>

        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: 0.75, columnGap: 1, '& dt': { color: 'text.secondary', fontSize: 13 }, '& dd': { m: 0, fontSize: 14 } }}>
          <dt>Od</dt><dd data-testid="szczegol-od">{chwilaWarszawa(iso(b.start))}</dd>
          <dt>Do</dt><dd data-testid="szczegol-do">{b.koniec === null ? 'bez daty końca' : chwilaWarszawa(iso(b.koniec))}</dd>
          <dt>Czas trwania</dt><dd data-testid="szczegol-trwanie">{czasTrwania(b.start, b.koniec)}</dd>
          {wylaczono ? (<><dt>Wyłączono</dt><dd>{chwilaWarszawa(wylaczono)}{z?.disabledBy ? ` · ${z.disabledBy}` : ''}</dd></>) : null}
          <dt>Priorytet</dt><dd>{b.priorytet}</dd>
          <dt>Zakres</dt><dd>{b.v1 ? 'Wszyscy (publikacja V1)' : ETYKIETA_ZAKRESU[b.zakres]}</dd>
          <dt>Utworzył</dt><dd>{(z?.createdBy ?? b.v1?.createdBy) || '—'}</dd>
          <dt>Kiedy utworzono</dt><dd>{chwilaWarszawa(z?.publishedAt ?? b.v1?.createdAt ?? null)}</dd>
          {z && typeof z.potentialRecipients === 'number' ? (<><dt>Odbiorcy</dt><dd>{z.potentialRecipients.toLocaleString('pl-PL')} potencjalnych</dd></>) : null}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Adresaci</Typography>
        <Box data-testid="szczegol-adresaci">
          {b.zakres === 'GLOBAL' ? <Typography variant="body2">Wszyscy użytkownicy</Typography> : (
            <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
              {b.adresaci.map((t) => (
                <Typography component="li" variant="body2" key={t.id} sx={{ wordBreak: 'break-word' }}>
                  {b.zakres === 'USER' ? etykietaOsoby(t.id, t.name, osoby) : t.name}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>

        {mojeKolizje.length ? (
          <Alert severity={mojeKolizje.some((k) => k.remis) ? 'error' : 'warning'} sx={{ mt: 2 }} data-testid="szczegol-kolizje">
            Nachodzi na tych samych adresatów w tym samym czasie:
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {mojeKolizje.map((k) => {
                const inny = bloki.find((x) => x.klucz === k.z);
                return inny ? (
                  <li key={k.z}>
                    <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none', textAlign: 'left' }} onClick={() => onWybierz(inny)}>
                      {inny.nazwa} · {oknoWarszawa(iso(inny.start), iso(inny.koniec), true)} · priorytet {inny.priorytet}
                    </Button>
                    {k.remis ? ' — ten sam priorytet: wynik niejednoznaczny' : ' — wygrywa wyższy priorytet'}
                  </li>
                ) : null;
              })}
            </ul>
          </Alert>
        ) : null}

        <Divider sx={{ my: 2 }} />
        <Stack spacing={1} data-testid="szczegol-akcje">
          {z ? <Button variant="outlined" onClick={i(() => akcje.historiaZastosowania(z))}>Historia zastosowania</Button> : null}
          {wBibliotece ? <Button variant="outlined" onClick={i(() => akcje.historiaSkorki(b.themeId))}>Historia skórki</Button> : null}
          {wBibliotece && mozePublikowac && z ? (
            <Button variant="outlined" disabled={!zapisDostepny} onClick={i(() => akcje.zarzadzaj(b.themeId))}>Edytuj — zastosowania tej skórki</Button>
          ) : null}
          {mozePublikowac && biezacy ? (
            <Button variant="outlined" color="warning" disabled={!zapisDostepny || trwa} onClick={() => { void wylacz(); }}>
              {trwa ? 'Wyłączam…' : b.v1 ? 'Wyłącz (V1)' : 'Wyłącz to zastosowanie'}
            </Button>
          ) : null}
          {wBibliotece && mozePublikowac ? (
            <Button variant="outlined" disabled={!zapisDostepny} onClick={i(() => akcje.zaplanuj(b.themeId, b.zakres))}>
              {mojeKolizje.length ? 'Zamień na tę skórkę…' : 'Zaplanuj tę skórkę ponownie'}
            </Button>
          ) : null}
          <Button variant="outlined" onClick={i(() => akcje.widokUzytkownika())}>Sprawdź widok użytkownika</Button>
          <Button variant="outlined" onClick={i(() => akcje.podglad(z?.revisionId ?? b.v1!.id, b.nazwa, z?.minEngineVersion ?? b.v1?.minEngineVersion ?? 1))}>Podgląd skórki</Button>
          {wBibliotece ? <Button onClick={i(() => akcje.pokazWBibliotece(b.themeId))}>Pokaż kartę w bibliotece ↓</Button> : null}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Zastosowanie nie jest edytowane w miejscu: zmiana terminu lub adresatów = „Wyłącz” + nowe zastosowanie (historia pokazuje obie zmiany).
            {mojeKolizje.length ? ' „Zamień na tę skórkę…” otwiera planowanie — przy kolizji okno zaproponuje zamianę w jednej operacji.' : ''}
          </Typography>
        </Stack>
      </Box>
    </Drawer>
  );
}
